import { Component, Input, Output, EventEmitter, OnInit, ViewChild } from "@angular/core";
import { AdminGridColumnsFactory } from "../admin-grid/admin-grid-columns-factory";
import { SelectionChangedEvent } from "devextreme/ui/data_grid";
import { CustomItemCreatingEvent } from "devextreme/ui/tag_box";
import { combineLatest, map } from "rxjs";
import { GroupViewModel } from "src/app/core/model/ContentManagement";
import { ToolbarItemFactory } from "../toolbar";
import { ToolbarItem } from "devextreme/ui/data_grid";
import { DxDataGridComponent } from "devextreme-angular";
import { MetadataService } from "src/app/services/metadata.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { brdCatchError } from "../message-utils";

@Component({
  selector: "brd-functionalities-grid",
  templateUrl: "./functionalities-grid.component.html",
  styleUrl: "./functionalities-grid.component.less",
  standalone: false
})
export class FunctionalitiesGridComponent implements OnInit {
  @Input() selectedRowKeys?: number[];
  @Input() enableDelete = false;
  @Input() customItems: ToolbarItem[] = [];
  @Output() selectionChanged = new EventEmitter<number[]>();
  @ViewChild(DxDataGridComponent) functionalitiesGrid: DxDataGridComponent;

  visible = false;

  functionalities$ = combineLatest([this.metadataService.functionalities$, this.metadataService.functionFilter$]).pipe(
    map(([functionalities, filterIds]) => {
      if (!filterIds) return functionalities;
      return functionalities.filter(x => filterIds.includes(x.id));
    })
  );
  groups: GroupViewModel[] = [];

  tagBoxOptions = {};
  toolbarItems: ToolbarItem[];

  visibleDelete = false;
  selectedFuncs = [];

  functionalityFormGroup = new FormGroup({
    id: new FormControl(0),
    name: new FormControl("", Validators.required),
    groups: new FormControl(null)
  });
  saveBtnOptions = {
    text: "Save",
    onClick: (): void => this.confirmChanges()
  };
  cancelBtnOptions = {
    text: "Cancel",
    onClick: (): void => {
      this.hidePopup();
    }
  };

  deleteBtnOptions = {
    text: "Yes",
    onClick: (): void => {
      this.deleteFunc();
    }
  };
  cancelDeleteBtnOptions = {
    text: "No",
    onClick: (): void => this.hideDeletePopup()
  };

  constructor(private metadataService: MetadataService) {}

  ngOnInit(): void {
    this.metadataService.groups$
      .pipe(
        map(res => {
          this.groups = res;
        })
      )
      .subscribe();

    this.toolbarItems = [
      ToolbarItemFactory.createButton({
        icon: "add",
        text: "Functionality",
        onClick: () => {
          this.addRow();
        }
      }),
      ...this.customItems
    ];
  }

  columnsFunctionalities = [
    AdminGridColumnsFactory.createTextColumn({ fieldName: "id", caption: "id" }, { visible: false, allowEditing: false }),
    AdminGridColumnsFactory.createTextColumn({ fieldName: "name", caption: "Name" }),
    AdminGridColumnsFactory.createTextColumn(
      { fieldName: "groups", caption: "Groups" },
      {
        calculateDisplayValue: (rowData: { groups: GroupViewModel[] }) => {
          return rowData.groups?.map(x => this.groups?.find(g => g.id === x.id)?.name)?.join(", ");
        }
      }
    ),
    AdminGridColumnsFactory.createButtonsColumn({
      buttons: [
        {
          icon: "edit",
          onClick: (e): void => {
            this.showPopup({ ...e.row.data });
          }
        }
      ]
    })
  ];

  hidePopup = (): void => {
    this.functionalityFormGroup.reset();
    this.visible = false;
  };

  confirmChanges(): void {
    if (this.functionalityFormGroup.invalid) {
      this.functionalityFormGroup.markAllAsTouched();
      return;
    }

    const { id, name, groups } = this.functionalityFormGroup.value;
    this.metadataService
      .addFunctionality({
        id,
        name,
        groups
      })
      .pipe(
        map(res => {
          this.hidePopup();
          return res;
        }),
        brdCatchError()
      )
      .subscribe();
  }

  onSelectionChanged(e: SelectionChangedEvent): void {
    if (this.enableDelete) {
      if (e.selectedRowKeys.length > 0 && this.toolbarItems.length === 1) {
        this.toolbarItems.push(
          ToolbarItemFactory.createButton({
            icon: "trash",
            text: "Delete",
            type: "danger",
            onClick: () => {
              this.selectedFuncs = this.functionalitiesGrid.instance.getSelectedRowsData().map(x => x.name);
              this.visibleDelete = true;
            }
          })
        );
      } else if (e.selectedRowKeys.length === 0 && this.toolbarItems.length > 1) {
        this.toolbarItems = this.toolbarItems.slice(0, 1);
      }
    }

    if (this.selectionChanged) this.selectionChanged.emit(e.selectedRowKeys);
  }

  addRow(): void {
    this.showPopup({});
  }

  showPopup(formData): void {
    this.functionalityFormGroup.setValue({
      id: formData?.id ?? 0,
      name: formData?.name ?? "",
      groups: formData?.groups ?? null
    });
    this.visible = true;
  }

  hideDeletePopup = (): void => {
    this.visibleDelete = false;
  };

  deleteFunc(): void {
    const funcIds = this.functionalitiesGrid.instance.getSelectedRowKeys();

    this.metadataService
      .deleteFunctionality(funcIds)
      .pipe(
        map(res => {
          this.visibleDelete = false;
          return res;
        })
      )
      .subscribe();
  }

  onCustomItemCreating(e: CustomItemCreatingEvent): void {
    if (e.text) {
      const newItem = {
        id: 0,
        name: e.text
      };
      e.customItem = newItem;
    }
  }
}
